/**
 * landingPageVersionConfig.jsx
 *
 * Version registry for AssetOfferLandingPage.
 *
 * Based on a line-by-line read of:
 *   AssetOfferLandingPage.jsx        → "default" config
 *   AssetOfferLandingPage_v0_9_5.jsx → "v0_9_5"  config  (preferred for ML)
 *
 * Each config exports a single `renderLayout(data, headerSlot)` function.
 * `data`       – the bag returned by useLandingPageData()
 * `headerSlot` – the already-rendered <AssetOfferHeader /> node (the parent
 *                passes it in because the header itself is not version-specific)
 *
 * Key differences between versions:
 *
 *   Feature                        default            v0_9_5
 *   ─────────────────────────────  ─────────────────  ───────────────────────
 *   Description rendering          ReactMarkdown       plain Typography + regex
 *   Language col (top card)        msLanguageList      msLanguageList (corpus/lcr)
 *                                  when !toolService   inputLanguage  (mlModel)
 *   intendedUse in categories      no                  yes
 *   Modality row (mlModel)         no                  yes
 *   Distribution card              !toolService →      corpus/lcr → DistributionDisplay
 *                                  DistributionDisplay mlModel    → ModelDistributionDisplay
 *                                  toolService →       toolService → SoftwareDistributionDisplay
 *                                  SoftwareDistrib.
 *   Language lists column          pivot/src/tgt/meta  + outputLanguage
 *   ModelPropertiesDisplay props   core fields only    + instructions, designSpecs,
 *                                                        requiresSoftware, finetuningMethodology,
 *                                                        hasBenchmark, hasTrainingImpact,
 *                                                        hasInferencingImpact
 *   AssetOfferHeader extra props   validationStatus    releaseDate, modified
 *                                  validationErrors
 *
 * Adding a new version: add one entry to LANDING_PAGE_VERSION_CONFIGS below.
 */

import {Grid, Stack, Box} from "@mui/material";
import ProcessingResourceDisplay from "../sharedLandingpageSections/ProcessingResourceDisplay";
import ModalityListDisplay from "../sharedLandingpageSections/ModalityListDisplay";
import LanguageListDisplay from "../sharedLandingpageSections/LanguageListDisplay";
import {keys} from "../../config/nameSpacesKeys";
import {useTranslator} from "../../context/TranslatorContext";
import {
  DescriptionSection,
  TopCard,
  DistributionCard,
  CategoriesSection,
  LanguageListsSection,
  ModelPropertiesSection,
  ProvenanceSection,
  DocumentationSection,
  OptionalBottomCard,
} from "./landingPageSections";

// ─────────────────────────────────────────────────────────────────────────────
// Shared inner-row helpers (called inside TopCard's middleSlot)
// ─────────────────────────────────────────────────────────────────────────────

function ToolServiceMiddleRow({data}) {
  const {messages} = useTranslator();
  const {inputContentResourceList, outputResourceList, defaultLanguage, mappings} = data;
  return (
    <Grid container spacing={4}>
      <Grid item sm={6}>
        <ProcessingResourceDisplay objList={inputContentResourceList} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={messages.landing_page_section_inputContentResource} />
      </Grid>
      <Grid item sm={6}>
        <ProcessingResourceDisplay objList={outputResourceList} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={messages.landing_page_section_outputResource} />
      </Grid>
    </Grid>
  );
}

function ModalityMiddleRow({data}) {
  const {messages} = useTranslator();
  const {hasInputModalityList, hasOutputModalityList, defaultLanguage, mappings} = data;
  return (
    <Grid container spacing={4}>
      <Grid item sm={6}>
        <ModalityListDisplay objList={hasInputModalityList} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={messages.landing_page_section_hasInputModality} />
      </Grid>
      <Grid item sm={6}>
        <ModalityListDisplay objList={hasOutputModalityList} defaultLanguage={defaultLanguage} mappings={mappings} topLabel={messages.landing_page_section_hasOutputModality} />
      </Grid>
    </Grid>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEFAULT version  (AssetOfferLandingPage.jsx)
//
// – description via ReactMarkdown
// – language col: msLanguageList when !toolService
// – no intendedUse, no modalities, no outputLanguage list
// – distribution: !toolService → DistributionDisplay | toolService → SoftwareDistribution
// – ModelPropertiesDisplay: core fields only
// ─────────────────────────────────────────────────────────────────────────────

function DefaultLayout({data, headerSlot}) {
  const {
    isToolService, isML,
    msLanguageList, getLabel, defaultLanguage, mappings,
    hasProvenance, hasDocumentation, hasModelProperties, columnCount,
  } = data;
  // Language column: msLanguageList when not toolService
  const languageSlot = !isToolService ? (
    <LanguageListDisplay
      languageList={msLanguageList.getLanguagesList()}
      defaultLanguage={defaultLanguage}
      mappings={mappings}
      topLabel={getLabel(keys.mslanguage, "language")}
    />
  ) : null;

  // Middle row inside top card
  const middleSlot = isToolService ? <ToolServiceMiddleRow data={data} /> : null;

  // Distribution card variant
  const distributionVariant = isToolService ? "softwareDistribution" : "distribution";

  // Right column of bottom card
  const rightSlot = (
    <Grid item sm={12 / columnCount}>
      {hasModelProperties && <ModelPropertiesSection data={data} showExtendedProps={false} />}
      {hasProvenance && <ProvenanceSection data={data} />}
      {hasDocumentation && <DocumentationSection data={data} />}
    </Grid>
  );

  return (
    <>
      {headerSlot}
      <Box component="section">
        <DescriptionSection data={{ ...data, defaultLanguage, mappings }} renderMode="plain" />
      </Box>
      <Grid container alignItems="flex-start" spacing={2} style={{ marginTop: "0px" }}>
        <TopCard data={data} languageSlot={languageSlot} middleSlot={middleSlot} showIntendedUse={false} />
        <DistributionCard data={data} variant={distributionVariant} />
        <OptionalBottomCard
          data={data}
          categoriesSlot={<CategoriesSection data={data} />}
          languageListsSlot={<LanguageListsSection data={data} showOutputLanguage={false} />}
          rightSlot={rightSlot}
        />
      </Grid>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// v0_9_5 version
//
// – language col: msLanguageList (corpus/lcr) | inputLanguage (mlModel)
// – Modality row for mlModel
// – ModelDistributionDisplay | toolService → Software
// – outputLanguage in language lists column
// – ModelPropertiesDisplay: extended props (instructions, designSpecs, benchmarks, env. impact)
// ─────────────────────────────────────────────────────────────────────────────

function V095Layout({data, headerSlot}) {
  const {
    isToolService, isML, isCorpus, isLcr,
    msLanguageList, inputLanguage, getLabel, defaultLanguage, mappings,
    hasProvenance, hasDocumentation, hasModelProperties, columnCount,
  } = data;
  // Language column: corpus/lcr → msLanguageList; mlModel → inputLanguage
  const languageSlot = (isCorpus || isLcr) ? (
    <LanguageListDisplay
      languageList={msLanguageList.getLanguagesList()}
      defaultLanguage={defaultLanguage}
      mappings={mappings}
      topLabel={getLabel(keys.mslanguage, "language")}
    />
  ) : isML ? (
    <LanguageListDisplay
      languageList={inputLanguage.getLanguagesList()}
      defaultLanguage={defaultLanguage}
      mappings={mappings}
      topLabel={getLabel(keys.inputLanguage, "inputLanguage")}
    />
  ) : null;

  // Middle row: toolService → ProcessingResources; mlModel → Modalities
  const middleSlot = isToolService ?
    <ToolServiceMiddleRow data={data} /> :
    isML ?
      <ModalityMiddleRow data={data} /> :
      null;

  const distributionVariant = isToolService ?
    "softwareDistribution" :
    isML ?
      "modelDistribution" :
      "distribution";

  // Right column of bottom card
  const rightSlot = (
    <Grid item sm={12 / columnCount}>
      {hasModelProperties && <ModelPropertiesSection data={data} showExtendedProps={true} />}
      {hasProvenance && <ProvenanceSection data={data} />}
      {hasDocumentation && <DocumentationSection data={data} />}
    </Grid>
  );

  return (
    <>
      {headerSlot}
      <Box component="section">
        <DescriptionSection data={{ ...data, defaultLanguage, mappings }} renderMode="plain" />
      </Box>
      <Grid container alignItems="flex-start" spacing={2} style={{ marginTop: "0px" }}>
        <TopCard data={data} languageSlot={languageSlot} middleSlot={middleSlot} showIntendedUse={true} />
        <DistributionCard data={data} variant={distributionVariant} />
        <OptionalBottomCard
          data={data}
          categoriesSlot={<CategoriesSection data={data} />}
          languageListsSlot={<LanguageListsSection data={data} showOutputLanguage={true} />}
          rightSlot={rightSlot}
        />
      </Grid>

    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

export const LANDING_PAGE_VERSION_CONFIGS = {
  default: {Layout: DefaultLayout},
  v0_9_5: {Layout: V095Layout},
};

// Returns the layout config for the given version string.
// ML models always use v0_9_5 layout regardless of version param (per requirements).
// All other lrTypes use "default" unless a specific version is passed.

export function getLandingPageVersionConfig(version, lrType) {
  const formattedKey = version ? `v${version.replaceAll(".", "_")}` : "default";
  // console.log(formattedKey, lrType);
  return LANDING_PAGE_VERSION_CONFIGS[formattedKey] ?? LANDING_PAGE_VERSION_CONFIGS.default;
}
